import { type MarkSpec, type NodeSpec, Schema } from 'prosemirror-model';
import { addListNodes } from 'prosemirror-schema-list';
import { tableNodes } from 'prosemirror-tables';
import { addSuggestionMarks } from 'prosemirror-suggestion-mode';

const CRAWFORD_TABLE_KINDS = new Set([
  'crawford-letter-meta',
  'crawford-contact',
  'crawford-action-plan',
]);

const baseNodes: Record<string, NodeSpec> = {
  doc: { content: 'block+' },

  paragraph: {
    content: 'inline*',
    group: 'block',
    attrs: {
      dir: { default: null },
      align: { default: null },
    },
    parseDOM: [{
      tag: 'p',
      getAttrs(node) {
        if (!(node instanceof HTMLElement)) return {};
        return {
          dir: node.getAttribute('dir'),
          align: node.style.textAlign || null,
        };
      },
    }],
    toDOM(node) {
      const attrs: Record<string, string> = {};
      if (node.attrs.dir) attrs.dir = node.attrs.dir as string;
      if (node.attrs.align) attrs.style = `text-align: ${node.attrs.align as string}`;
      return ['p', attrs, 0];
    },
  },

  heading: {
    content: 'inline*',
    group: 'block',
    defining: true,
    attrs: {
      level: { default: 1 },
    },
    parseDOM: [1, 2, 3, 4, 5, 6].map((level) => ({
      tag: `h${level}`,
      attrs: { level },
    })),
    toDOM(node) {
      return [`h${node.attrs.level as number}`, 0];
    },
  },

  blockquote: {
    content: 'block+',
    group: 'block',
    defining: true,
    parseDOM: [{ tag: 'blockquote' }],
    toDOM() { return ['blockquote', 0]; },
  },

  code_block: {
    content: 'text*',
    group: 'block',
    code: true,
    defining: true,
    marks: '',
    parseDOM: [{ tag: 'pre', preserveWhitespace: 'full' }],
    toDOM() { return ['pre', ['code', 0]]; },
  },

  hard_break: {
    inline: true,
    group: 'inline',
    selectable: false,
    parseDOM: [{ tag: 'br' }],
    toDOM() { return ['br']; },
  },

  quote: {
    inline: true,
    group: 'inline',
    content: 'inline*',
    attrs: {
      cite: { default: null },
      lang: { default: null },
      dir: { default: null },
    },
    parseDOM: [
      ...['q', 'span[data-quote]'].map((tag) => ({
        tag,
        getAttrs(node: HTMLElement | string) {
          if (!(node instanceof HTMLElement)) return {};
          return {
            cite: node.getAttribute('cite'),
            lang: node.getAttribute('lang'),
            dir: node.getAttribute('dir'),
          };
        },
      })),
      // The clipboard form (see clipboardSerializer) spells its quotation marks out;
      // pasting it back drops them so they are not doubled.
      { tag: 'span[data-quote-mark]', ignore: true },
    ],
    toDOM(node) {
      const attrs: Record<string, string> = {};
      if (node.attrs.cite) attrs.cite = node.attrs.cite as string;
      if (node.attrs.lang) attrs.lang = node.attrs.lang as string;
      if (node.attrs.dir) attrs.dir = node.attrs.dir as string;
      return ['q', attrs, 0];
    },
  },

  page_break: {
    group: 'block',
    atom: true,
    selectable: false,
    parseDOM: [{
      tag: 'div.page-break',
      getAttrs(node) {
        return node instanceof HTMLElement && node.className === 'page-break' ? {} : false;
      },
    }],
    toDOM() {
      return ['div', { class: 'page-break', style: 'page-break-before: always' }];
    },
  },

  image: {
    inline: true,
    group: 'inline',
    draggable: true,
    attrs: {
      src: {},
      alt: { default: null },
      title: { default: null },
      /**
       * Displayed size in pixels. Carried on the node, not left to the renderer: an image with no
       * stated size is drawn by the Word converter at the full width of the page, which is how
       * every inserted screenshot arrived a full page wide.
       */
      width: { default: null },
      height: { default: null },
    },
    parseDOM: [{
      tag: 'img[src]',
      getAttrs(node) {
        if (!(node instanceof HTMLElement)) return false;
        return {
          src: node.getAttribute('src'),
          alt: node.getAttribute('alt'),
          title: node.getAttribute('title'),
          width: readPixels(node.getAttribute('width')),
          height: readPixels(node.getAttribute('height')),
        };
      },
    }],
    toDOM(node) {
      const attrs: Record<string, string> = { src: node.attrs.src as string };
      if (node.attrs.alt) attrs.alt = node.attrs.alt as string;
      if (node.attrs.title) attrs.title = node.attrs.title as string;
      // Written as attributes rather than a style, because that is what the Word converter reads.
      if (node.attrs.width) attrs.width = String(node.attrs.width);
      if (node.attrs.height) attrs.height = String(node.attrs.height);
      return ['img', attrs];
    },
  },

  text: { group: 'inline' },
};

/** A pixel count from an HTML attribute. Percentages and junk are ignored rather than guessed at. */
function readPixels(value: string | null): number | null {
  // A plain integer only. parseInt happily reads "100%" as 100, which would turn a full-width
  // image into a hundred-pixel thumbnail rather than leaving it unsized.
  if (!value || !/^\d+$/u.test(value.trim())) return null;
  const pixels = Number(value);
  return pixels > 0 ? pixels : null;
}

const baseMarks: Record<string, MarkSpec> = {
  link: {
    inclusive: false,
    attrs: {
      href: { default: '' },
      title: { default: null },
    },
    parseDOM: [{
      tag: 'a[href]',
      getAttrs(node) {
        if (!(node instanceof HTMLElement)) return false;
        return {
          href: node.getAttribute('href'),
          title: node.getAttribute('title'),
        };
      },
    }],
    toDOM(mark) {
      const attrs: Record<string, string> = { href: mark.attrs.href as string };
      if (mark.attrs.title) attrs.title = mark.attrs.title as string;
      return ['a', attrs, 0];
    },
  },

  bold: {
    parseDOM: [{ tag: 'strong' }, { tag: 'b' }, { style: 'font-weight=bold' }],
    toDOM() { return ['strong', 0]; },
  },

  italic: {
    parseDOM: [{ tag: 'em' }, { tag: 'i' }, { style: 'font-style=italic' }],
    toDOM() { return ['em', 0]; },
  },

  underline: {
    parseDOM: [{ tag: 'u' }, { style: 'text-decoration=underline' }],
    toDOM() { return ['u', 0]; },
  },

  strike: {
    parseDOM: [{ tag: 's' }, { tag: 'strike' }, { style: 'text-decoration=line-through' }],
    toDOM() { return ['s', 0]; },
  },

  code: {
    parseDOM: [{ tag: 'code' }],
    toDOM() { return ['code', 0]; },
  },

  font_size: {
    attrs: { size: { default: null } },
    parseDOM: [{
      tag: 'span[style*="font-size"]',
      getAttrs(node) {
        if (!(node instanceof HTMLElement)) return false;
        const match = node.style.fontSize.match(/(\d+(?:\.\d+)?)px/);
        if (!match) return false;
        return { size: Number(match[1]) };
      },
    }],
    toDOM(mark) {
      const size = mark.attrs.size as number | null;
      if (!size) return ['span', 0];
      return ['span', { style: `font-size: ${size}px` }, 0];
    },
  },

  font_family: {
    attrs: { family: { default: null } },
    parseDOM: [{
      tag: 'span[style*="font-family"]',
      getAttrs(node) {
        if (!(node instanceof HTMLElement)) return false;
        const fam = node.style.fontFamily;
        if (!fam) return false;
        return { family: fam };
      },
    }],
    toDOM(mark) {
      const family = mark.attrs.family as string | null;
      if (!family) return ['span', 0];
      return ['span', { style: `font-family: ${family}` }, 0];
    },
  },
};

const draftSchema = new Schema({ nodes: baseNodes, marks: baseMarks });
const nodesWithLists = addListNodes(draftSchema.spec.nodes, 'paragraph block*', 'block');

const tableNodeMap = tableNodes({
  tableGroup: 'block',
  cellContent: 'block+',
  cellAttributes: {},
});
const tableNodeSpec = tableNodeMap.table;
if (!tableNodeSpec) {
  throw new Error('prosemirror-tables did not provide a table node');
}
tableNodeMap.table = {
  ...tableNodeSpec,
  attrs: {
    ...tableNodeSpec.attrs,
    tableKind: { default: null },
  },
  parseDOM: [{
    tag: 'table',
    getAttrs(node) {
      if (!(node instanceof HTMLElement)) return { tableKind: null };
      const tableKind = node.getAttribute('data-table-kind');
      return {
        tableKind: tableKind && CRAWFORD_TABLE_KINDS.has(tableKind) ? tableKind : null,
      };
    },
  }],
  toDOM(node) {
    const tableKind = node.attrs.tableKind as string | null;
    if (!tableKind || !CRAWFORD_TABLE_KINDS.has(tableKind)) {
      return ['table', ['tbody', 0]];
    }
    return [
      'table',
      { 'data-table-kind': tableKind, style: 'width: 100%' },
      ['tbody', 0],
    ];
  },
};

let nodesWithTables = nodesWithLists;
for (const [name, spec] of Object.entries(tableNodeMap)) {
  nodesWithTables = nodesWithTables.addToEnd(name, spec);
}

export const schema = new Schema({
  nodes: nodesWithTables,
  marks: addSuggestionMarks(baseMarks),
});
